import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SimpleLayout from '../../../layouts/SimpleLayout';
import { Spinner } from 'react-bootstrap';
import logo from '../../../assets/img/logo.png';
import { formatAxiosError } from '../../../utils/formatAxiosError';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2';
import apiClient from '../../../services/api/apiClient';
import AlertComponent from '../../../components/utility/AlertComponent';
import IAlertType from '../../../utils/types/props/IAlertType';
import { useMutation } from '@tanstack/react-query';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('userId');
    const token = searchParams.get('token');

    // ---

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);

    // ---

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);

    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };

    // ---

    const { mutate, isPending: isOperationProgress } = useMutation({
        mutationFn: () =>
            apiClient.post('/api/auth/reset-password', {
                userId,
                token,
                newPassword,
                confirmPassword,
            }),
        onSuccess: (response) => {
            if (response.data.success) {
                Swal.fire({
                    text: 'Mot de passe réinitialisé avec succès',
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
                setResetSuccess(true);
            }
        },
        onError: (err) => {
            setSystemAlerts((prev) => [
                ...prev,
                {
                    type: 'danger',
                    message: formatAxiosError(err)?.message || 'Erreur lors de la réinitialisation du mot de passe',
                },
            ]);
        },
    });

    // ---

    const validateForm = () => {
        if (!newPassword || !confirmPassword) {
            Swal.fire({
                text: 'Veuillez remplir tous les champs',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return false;
        }

        if (newPassword !== confirmPassword) {
            Swal.fire({
                text: 'Les mots de passe ne correspondent pas',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return false;
        }

        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm() || !userId || !token) return;

        mutate();
    };

    return (
        <>
            <Helmet>
                <title>{resetSuccess ? 'Réinitialisation réussie' : 'Réinitialisation du mot de passe'} :: Portail PETROSIL</title>
            </Helmet>

            <div className="page page-center">
                <div className="container container-tight py-4">
                    <div className="text-center mb-4">
                        <a href="/" className="navbar-brand navbar-brand-autodark">
                            <img src={logo} alt="Logo" width="150" height="auto" />
                        </a>
                    </div>

                    {!userId || !token ? (
                        <>
                            <div className="alert alert-important alert-danger alert-dismissible" role="alert">
                                <div className="alert-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon alert-icon icon-2">
                                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                                        <path d="M12 8v4"></path>
                                        <path d="M12 16h.01"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="alert-heading">Erreur des paramètres</h4>
                                    <div className="alert-description">Erreur des paramètres de réinitialisation de mot de passe.</div>
                                </div>
                            </div>

                            <div className="hr-text">#</div>

                            <div>
                                <div className="text-center text-secondary mt-3">
                                    <a href="/login">Connexion</a>.
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {!resetSuccess ? (
                                <div className="card card-md">
                                    <div className="card-body">
                                        <h2 className="card-title text-center mb-4">Réinitialiser le mot de passe</h2>

                                        <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />

                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <label className="form-label">Nouveau mot de passe</label>
                                                <input type="password" className="form-control" placeholder="Entrez votre nouveau mot de passe" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoFocus />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Confirmez le mot de passe</label>
                                                <input type="password" className="form-control" placeholder="Confirmez votre nouveau mot de passe" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                            </div>
                                            <div className="form-footer">
                                                <button type="submit" className="btn btn-primary w-100" disabled={isOperationProgress}>
                                                    {isOperationProgress ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" />
                                                            <span className="ms-2">Traitement...</span>
                                                        </>
                                                    ) : (
                                                        'Réinitialiser le mot de passe'
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            ) : (
                                <div className="card card-md">
                                    <div className="card-body text-center">
                                        <h2 className="card-title mb-4">Réinitialisation réussie !</h2>
                                        <p className="text-secondary mb-4">Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
                                        <a href="/login" className="btn btn-primary w-100">
                                            Se connecter
                                        </a>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

ResetPassword.Layout = SimpleLayout;

export default ResetPassword;
